import { Link } from "react-router-dom";
import SharedLayout from "../components/SharedLayout";
import useSEO from "../utils/useSEO";
import { HeroIllo, ClaimGuardIllo } from "../components/AnimatedIllustrations";

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);
const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
    <path d="M5 13l4 4L19 7" />
  </svg>
);
const GlowOrb = ({ className }) => (
  <div className={`absolute rounded-full blur-3xl opacity-20 pointer-events-none ${className}`} />
);
const Sparkle = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />
    <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15z" />
  </svg>
);

// ─── The wedge: pre-billing validation checks ────────────────────────────────
const DENIAL_CHECKS = [
  "Authorization number",
  "Rider signature",
  "EVV capture",
  "Mileage accuracy",
  "HCPCS codes & modifiers",
];

// ─── Platform pillars ────────────────────────────────────────────────────────
const PILLARS = [
  {
    badge: "The Wedge",
    title: "Billing & Denial Engine",
    tagline: "Get paid for every trip you run",
    accent: "text-emerald-400",
    ring: "border-emerald-500/40 bg-emerald-500/[0.06]",
    dot: "bg-emerald-400",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M12 2l7 4v5c0 4.418-3.134 8.566-7 9.95C8.134 19.566 5 15.418 5 11V6l7-4z" />
        <path d="M9 11l2 2 4-4" />
      </svg>
    ),
    points: [
      "Denial Shield: every claim is auto-checked for auth #, signature, EVV, mileage, and correct HCPCS codes/modifiers before it can be submitted.",
      "Broker & Medicaid claim generation (837P / CMS-1500) with per-broker rule packs — ModivCare, MTM, SafeRide, Verida, Access2Care.",
      "Denial management: reason capture, guided fixes, one-click resubmission, and recovered-revenue tracking.",
      "Trip-to-claim reconciliation, AR aging, and payment posting — nothing under-billed or lost.",
      "Private-pay & facility invoicing (dialysis centers, nursing homes) with recurring billing.",
      "Rate & fee-schedule engine by broker, service type (ambulatory / wheelchair / stretcher), mileage, and wait-time.",
    ],
  },
  {
    title: "Manager & Dispatch Dashboard",
    tagline: "Run the whole operation from one screen",
    accent: "text-teal-400",
    ring: "border-teal-500/30 bg-teal-500/[0.04]",
    dot: "bg-teal-400",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18M9 21V9" />
      </svg>
    ),
    points: [
      "Drag-and-drop, multi-vehicle scheduling with recurring standing orders (e.g. dialysis 3×/week) and conflict detection.",
      "AI auto-dispatch: best-fit driver & vehicle by proximity, service type, and availability — with multi-load grouping.",
      "Live operations map showing every vehicle and trip in real time, with missed-pickup and exception alerts.",
      "Trip import from broker feeds, plus will-call and same-day handling.",
      "Time & attendance from EVV data, payroll/accounting export, role-based permissions, and a full audit trail.",
    ],
  },
  {
    title: "Driver Mobile App",
    tagline: "iOS + Android · built for the field",
    accent: "text-cyan-400",
    ring: "border-cyan-500/30 bg-cyan-500/[0.04]",
    dot: "bg-cyan-400",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <rect x="5" y="2" width="14" height="20" rx="2" />
        <path d="M12 18h.01M9 5h6" />
      </svg>
    ),
    points: [
      "Today's manifest, turn-by-turn navigation, and trip status with large, low-training controls.",
      "EVV capture at pickup and drop-off (GPS + timestamp) with rider signature at point of service.",
      "Offline mode with auto-sync, photo capture, and in-app messaging with dispatch.",
      "License & certification wallet with renewal reminders and an hours summary.",
    ],
  },
  {
    title: "Client & Facility Portal",
    tagline: "Proof of service that defends contracts",
    accent: "text-sky-400",
    ring: "border-sky-500/30 bg-sky-500/[0.04]",
    dot: "bg-sky-400",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />
      </svg>
    ),
    points: [
      "Facilities (dialysis, clinics, nursing homes) request and track trips directly, with branded activity reports.",
      "GPS-verified proof of trips and on-time performance — the report operators use to defend their contracts.",
      "HIPAA-compliant architecture with signed BAAs and multi-location support.",
      "Open API, SMS reminders, and a multilingual UI across the whole platform.",
    ],
  },
];

// ─── AI capabilities ─────────────────────────────────────────────────────────
const AI_FEATURES = [
  {
    headline: true,
    title: "AI Denial Prevention",
    desc: "Learns from your past denials to flag claims likely to be rejected — risky payer, code, and service-combination patterns, not just missing fields — before they're ever submitted. Denial reasons are auto-classified with the exact fix suggested, turning a manual chase into a one-click resubmission.",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M12 2l7 4v5c0 4.418-3.134 8.566-7 9.95C8.134 19.566 5 15.418 5 11V6l7-4z" />
        <path d="M12 8v4M12 15h.01" />
      </svg>
    ),
  },
  {
    title: "AI-Assisted Dispatch & Routing",
    desc: "Auto-generates draft daily schedules that respect service type, capacity, and time windows, and multi-loads compatible trips to cut deadhead miles. It also predicts no-show and will-call risk from history, so dispatch lines up backup before it becomes an emergency.",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <circle cx="6" cy="19" r="2" /><circle cx="18" cy="5" r="2" />
        <path d="M8 19h6a4 4 0 0 0 0-8H8a4 4 0 0 1 0-8h4" />
      </svg>
    ),
  },
  {
    title: "AI Trip Intake & Automations",
    desc: "Reads broker trip files and portals and structures them into clean trips automatically — no manual entry. Ask for analytics in plain English (\"show every denied dialysis trip last month for ModivCare\"), get auto-generated monthly client summaries, multi-stop route optimization, and multilingual voice-to-text field notes.",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M4 4h16v12H5.17L4 17.17V4z" /><path d="M8 8h8M8 11h5" />
      </svg>
    ),
  },
];

// ─── Pricing ─────────────────────────────────────────────────────────────────
const TIERS = [
  {
    name: "Starter",
    range: "1–5 vehicles",
    price: "$49",
    unit: "/vehicle · mo",
    note: "Minimum $149/mo",
    accent: "text-emerald-400",
    ring: "border-white/10",
    button: "border border-white/15 text-white hover:bg-white/5",
  },
  {
    name: "Growth",
    range: "6–20 vehicles",
    price: "$39",
    unit: "/vehicle · mo",
    note: "Most popular fleet size",
    popular: true,
    accent: "text-emerald-400",
    ring: "border-emerald-500/40 bg-emerald-500/[0.05]",
    button: "bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:-translate-y-0.5 shadow-lg shadow-emerald-900/40",
  },
  {
    name: "Fleet",
    range: "21–75 vehicles",
    price: "$32",
    unit: "/vehicle · mo",
    note: "Best per-vehicle rate",
    accent: "text-emerald-400",
    ring: "border-white/10",
    button: "border border-white/15 text-white hover:bg-white/5",
  },
];

const PRICING_INCLUDES = [
  "Billing & denial engine — no separate claims fees",
  "All broker rule packs (ModivCare, MTM & more)",
  "Manager dashboard + driver apps (iOS & Android)",
  "Client/facility portal & unlimited users",
  "AI denial prevention & auto-dispatch",
  "HIPAA hosting, BAAs & onboarding support",
];

const PROCESS = [
  { step: "01", title: "Operations Discovery", desc: "We map your trip volume, broker contracts, vehicle types, and billing rules to fit how you actually dispatch." },
  { step: "02", title: "Workflow & UX Design", desc: "Dispatcher boards, driver flows, and facility portals prototyped and validated with your team before build." },
  { step: "03", title: "Agile Build", desc: "Two-week sprints with deployed previews. Scheduling, dispatch, driver app, and billing ship incrementally." },
  { step: "04", title: "Integrations & Compliance", desc: "Broker EDI, mapping, telephony, and payments wired in — with HIPAA safeguards and audit logs from day one." },
  { step: "05", title: "Launch & Support", desc: "Data migration, dispatcher training, and phased rollout, followed by monitoring, SLAs, and ongoing iteration." },
];

export default function NEMTDetail() {
  useSEO({
    title: "NEMT Software & Billing Services in Texas | ClaimGuard",
    description: "ClaimGuard NEMT — non-emergency medical transportation software in Texas with denial-prevention billing, AI dispatch & claims management (837P / CMS-1500).",
    keywords: "NEMT services Texas, NEMT billing services Texas, NEMT claims management Texas, NEMT software Texas, non-emergency medical transportation Texas, ClaimGuard NEMT, Medicaid transportation billing 837P CMS-1500",
    canonical: "https://www.berrybyteinn.com/services/nemt-solutions",
  });

  return (
    <SharedLayout>
      {/* ─── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-20">
        <GlowOrb className="w-[700px] h-[700px] bg-emerald-600 -top-32 -left-40" />
        <GlowOrb className="w-[500px] h-[500px] bg-teal-700 bottom-0 -right-32" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

        {/* decorative animated illustration */}
        <HeroIllo>
          <ClaimGuardIllo />
        </HeroIllo>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <a href="/services" className="inline-flex items-center gap-2 text-slate-500 hover:text-emerald-400 text-sm font-medium transition-colors duration-200 mb-8">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
            Services / ClaimGuard NEMT
          </a>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-sm font-medium mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              Now accepting new NEMT operators · US &amp; Canada
            </div>

            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight mb-6">
              ClaimGuard NEMT<br />
              <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">Stop losing money to denials.</span>
            </h1>

            <p className="text-xl text-slate-400 leading-relaxed max-w-2xl mb-8">
              The all-in-one platform for Non-Emergency Medical Transportation. We catch billing errors <span className="text-slate-200 font-semibold">before</span> claims go out, auto-dispatch your fleet, and give facilities GPS-verified proof of every trip — so you get paid for the work you already do.
            </p>

            {/* Founding-customer offer */}
            <div className="max-w-2xl mb-10 rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.1] to-teal-500/[0.02] p-6 backdrop-blur-sm">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[11px] font-bold uppercase tracking-widest mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Founding Customer Offer
              </div>
              <p className="text-slate-300 leading-relaxed mb-3">
                Normally each plan unlocks different features — but as one of our founding customers, that restriction is removed for you. You get the <span className="text-white font-semibold">full software, completely unrestricted, for life</span>, no matter which plan you're on.
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {["No setup cost", "No lock-in", "Free migration", "2 months free to start"].map((perk) => (
                  <span key={perk} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-emerald-500/20 text-emerald-200 text-xs font-medium">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-3 h-3 text-emerald-400"><path d="M5 13l4 4L19 7" /></svg>
                    {perk}
                  </span>
                ))}
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                We're booking a small number of slots for our initial customers before public launch — <span className="text-emerald-300 font-semibold">we'd love to reserve one for you.</span>
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold text-base shadow-2xl shadow-emerald-900/40 hover:shadow-emerald-900/60 hover:-translate-y-1 transition-all duration-300">
                Book Now
                <span className="group-hover:translate-x-1 transition-transform duration-200"><ArrowRight /></span>
              </Link>
              <a href="#pricing" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl border border-white/10 text-white font-semibold bg-white/5 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
                See Pricing
              </a>
            </div>

            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8 pt-12 border-t border-white/5">
              {[{ value: "Denial", label: "Shield on Every Claim" }, { value: "837P", label: "/ CMS-1500 Ready" }, { value: "35%", label: "Fewer Deadhead Miles" }, { value: "HIPAA", label: "Compliant by Design" }].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-2xl lg:text-3xl font-black text-white mb-1">{s.value}</div>
                  <div className="text-sm text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── The Wedge: Denial Shield ─────────────────────────────────────── */}
      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[600px] h-[600px] bg-emerald-600 top-1/2 -translate-y-1/2 -left-40" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.08] to-teal-500/[0.02] p-8 lg:p-12 overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-bold uppercase tracking-widest mb-5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> The Wedge
                </div>
                <h2 className="text-3xl lg:text-5xl font-black text-white mb-5 leading-tight">
                  A <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">Denial Shield</span> on every claim.
                </h2>
                <p className="text-slate-400 text-lg leading-relaxed mb-6">
                  Most NEMT revenue leaks through rejected claims. Before anything is submitted, ClaimGuard validates each claim against payer rules — so denials get caught at your desk, not weeks later at the broker.
                </p>
                <p className="text-slate-500 text-sm">
                  Every claim is auto-checked for:
                </p>
              </div>
              <div className="flex flex-col gap-3">
                {DENIAL_CHECKS.map((c, i) => (
                  <div key={c} className="flex items-center gap-4 p-4 rounded-2xl bg-[#050810]/60 border border-emerald-500/15 hover:border-emerald-500/40 transition-colors duration-300">
                    <span className="flex-shrink-0 w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400"><Check /></span>
                    <span className="text-slate-200 font-medium">{c}</span>
                    <span className="ml-auto text-emerald-500/60 text-xs font-mono">0{i + 1}</span>
                  </div>
                ))}
                <p className="text-emerald-300/80 text-sm mt-2 pl-1">
                  → Fails the check? It can't be submitted until it's fixed.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Platform pillars ─────────────────────────────────────────────── */}
      <section className="relative py-24 bg-[#050810] overflow-hidden">
        <GlowOrb className="w-[500px] h-[500px] bg-teal-700 -top-24 -right-32" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-emerald-400 font-semibold text-sm tracking-widest uppercase">The Platform</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">One system, four moving parts<br /><span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">that all talk to each other.</span></h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className={`relative rounded-3xl border ${p.ring} p-8 hover:-translate-y-1 transition-transform duration-300`}>
                  {p.badge && (
                    <span className="absolute top-6 right-6 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[10px] font-bold uppercase tracking-widest">
                      {p.badge}
                    </span>
                  )}
                  <div className={`inline-flex p-3 rounded-xl bg-white/5 border border-white/10 ${p.accent} mb-4`}><Icon /></div>
                  <h3 className="text-xl font-black text-white mb-1">{p.title}</h3>
                  <div className={`text-xs font-medium tracking-wide mb-5 ${p.accent}`}>{p.tagline}</div>
                  <ul className="flex flex-col gap-3">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-3">
                        <span className={`flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full ${p.dot}`} />
                        <span className="text-slate-400 text-sm leading-relaxed">{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── AI Built In ──────────────────────────────────────────────────── */}
      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[600px] h-[600px] bg-emerald-700 bottom-0 left-1/3" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 text-emerald-400 mb-3"><Sparkle /></div>
            <h2 className="text-4xl lg:text-5xl font-black text-white">AI where it actually pays off.</h2>
            <p className="mt-4 text-slate-400 max-w-2xl mx-auto">Not a gimmick — AI is pointed straight at the two things that cost NEMT operators the most: denied claims and wasted miles.</p>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            {AI_FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className={`relative rounded-3xl p-8 hover:-translate-y-1 transition-transform duration-300 ${
                    f.headline
                      ? "border border-emerald-500/40 bg-gradient-to-br from-emerald-500/[0.1] to-teal-500/[0.02] lg:row-span-1"
                      : "border border-white/8 bg-white/[0.02]"
                  }`}
                >
                  {f.headline && (
                    <span className="absolute top-6 right-6 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-[10px] font-bold uppercase tracking-widest">
                      Headline
                    </span>
                  )}
                  <div className={`inline-flex p-3 rounded-xl bg-white/5 border border-white/10 mb-4 ${f.headline ? "text-emerald-400" : "text-teal-400"}`}><Icon /></div>
                  <h3 className="text-lg font-bold text-white mb-3">{f.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
          <p className="text-center text-slate-600 text-xs mt-8 max-w-2xl mx-auto">
            Built pragmatically: v1 runs on a proven LLM with a claims-rules prompt and a retrieval layer over your own denial history — no custom model training required.
          </p>
        </div>
      </section>

      {/* ─── Pricing ──────────────────────────────────────────────────────── */}
      <section id="pricing" className="relative py-24 bg-[#050810] overflow-hidden scroll-mt-24">
        <GlowOrb className="w-[700px] h-[700px] bg-emerald-700 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-6">
            <span className="text-emerald-400 font-semibold text-sm tracking-widest uppercase">Pricing</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">Priced per active vehicle.</h2>
          </div>
          <div className="flex justify-center mb-14">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-sm font-semibold">
              <Check /> Billing included · No separate claims fees · No per-trip charges
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {TIERS.map((t) => (
              <div
                key={t.name}
                className={`relative rounded-3xl border ${t.ring} p-8 flex flex-col ${t.popular ? "md:-translate-y-3 shadow-2xl shadow-emerald-900/30" : ""}`}
              >
                {t.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-[10px] font-bold uppercase tracking-widest shadow-lg">
                    Most Popular
                  </span>
                )}
                <div className="text-sm font-bold text-white mb-1">{t.name}</div>
                <div className="text-xs text-slate-500 mb-6">{t.range}</div>
                <div className="flex items-end gap-1 mb-1">
                  <span className={`text-5xl font-black ${t.accent}`}>{t.price}</span>
                  <span className="text-slate-500 text-sm mb-2">{t.unit}</span>
                </div>
                <div className="text-xs text-slate-500 mb-8">{t.note}</div>
                <Link
                  to="/contact"
                  className={`mt-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${t.button}`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>

          {/* Everything included */}
          <div className="mt-12 max-w-4xl mx-auto rounded-3xl border border-white/8 bg-white/[0.02] p-8">
            <div className="text-center text-sm font-semibold text-white mb-6">Every plan includes the full platform</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {PRICING_INCLUDES.map((f) => (
                <div key={f} className="flex items-center gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400"><Check /></span>
                  <span className="text-slate-300 text-sm">{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── How We Work ──────────────────────────────────────────────────── */}
      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-teal-400 font-semibold text-sm tracking-widest uppercase">How We Work</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">Built around your operations.<br /><span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">No guesswork.</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {PROCESS.map((step) => (
              <div key={step.step} className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 hover:-translate-y-1 transition-transform duration-300">
                <div className="text-xs font-bold text-emerald-500 tracking-widest mb-2">{step.step}</div>
                <h3 className="text-sm font-bold text-white mb-2">{step.title}</h3>
                <p className="text-slate-400 text-xs leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ──────────────────────────────────────────────────────────── */}
      <section className="relative py-24 bg-[#050810] overflow-hidden">
        <GlowOrb className="w-[700px] h-[700px] bg-emerald-700 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-6xl font-black text-white mb-6">
            Run a NEMT company?<br />
            <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">See ClaimGuard on your own trips.</span>
          </h2>
          <p className="text-slate-400 text-lg mb-10 leading-relaxed">
            Book a free 30-minute demo. Bring a batch of recent claims and we'll show you, live, how many the Denial Shield would have caught.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold shadow-2xl shadow-emerald-900/40 hover:shadow-emerald-900/60 hover:-translate-y-1 transition-all duration-300">
              Book Now<span className="group-hover:translate-x-1 transition-transform duration-200"><ArrowRight /></span>
            </Link>
            <a href="/services" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl border border-white/10 text-white font-semibold bg-white/5 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
              Explore Other Services
            </a>
          </div>
        </div>
      </section>
    </SharedLayout>
  );
}
